import { Link } from '@tanstack/react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  ShieldCheck, 
  ShieldAlert, 
  ShieldX, 
  CreditCard, 
  Clock, 
  ArrowRight,
  AlertTriangle,
  ExternalLink
} from 'lucide-react';
import { useLicenseStore } from '@/stores/licenseStore';
import { cn } from '@/lib/utils';
import { isTauri } from '@/lib/tauri';

/**
 * License Status Card - Shows current license/subscription status
 * Clickable card that routes to subscription page
 * Works for both Desktop and Cloud versions
 */
export function LicenseStatusCard() {
  const { licenseState, license, daysUntilExpiry: getDaysUntilExpiry } = useLicenseStore();
  const isDesktop = isTauri();
  const daysUntilExpiry = typeof getDaysUntilExpiry === 'function' ? getDaysUntilExpiry() : getDaysUntilExpiry;

  const getStatusConfig = () => {
    switch (licenseState) {
      case 'valid':
        return {
          icon: ShieldCheck,
          title: isDesktop ? 'Licence Active' : 'Abonnement Actif',
          description: isDesktop 
            ? 'Votre licence desktop est valide et active'
            : 'Votre abonnement cloud est actif',
          badge: 'Actif',
          badgeVariant: 'default' as const,
          iconColor: 'text-green-500',
          bgColor: 'bg-green-500/10',
          borderColor: 'border-green-200 dark:border-green-800',
          urgent: false,
        };
      case 'verification_needed':
        return {
          icon: ShieldAlert,
          title: 'Vérification Requise',
          description: 'Une vérification de licence est nécessaire',
          badge: 'Action requise',
          badgeVariant: 'secondary' as const,
          iconColor: 'text-amber-500',
          bgColor: 'bg-amber-500/10',
          borderColor: 'border-amber-200 dark:border-amber-800',
          urgent: true,
        };
      case 'expired':
        return {
          icon: ShieldX,
          title: isDesktop ? 'Licence Expirée' : 'Abonnement Expiré',
          description: 'Renouvelez pour continuer à utiliser le service',
          badge: 'Expiré',
          badgeVariant: 'destructive' as const,
          iconColor: 'text-red-500',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-200 dark:border-red-800',
          urgent: true,
        };
      default:
        return {
          icon: ShieldCheck,
          title: 'Statut Licence',
          description: 'Consultez les détails de votre abonnement',
          badge: 'Info',
          badgeVariant: 'outline' as const,
          iconColor: 'text-gray-500',
          bgColor: 'bg-gray-500/10',
          borderColor: 'border-gray-200 dark:border-gray-800',
          urgent: false,
        };
    }
  };

  const config = getStatusConfig();
  const StatusIcon = config.icon;

  // Calculate urgency based on days left
  const isUrgent = daysUntilExpiry !== null && daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  const isExpired = daysUntilExpiry !== null && daysUntilExpiry <= 0;

  return (
    <Link to="/settings/subscription" className="block">
      <Card className={cn(
        "cursor-pointer transition-all hover:shadow-md hover:border-primary/50",
        config.borderColor,
        (config.urgent || isUrgent || isExpired) && "ring-2 ring-red-500/20"
      )}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={cn("p-2 rounded-lg", config.bgColor)}>
                <StatusIcon className={cn("h-6 w-6", config.iconColor)} />
              </div>
              <div>
                <CardTitle className="text-lg">{config.title}</CardTitle>
                <CardDescription className="text-sm">
                  {config.description}
                </CardDescription>
              </div>
            </div>
            <Badge variant={config.badgeVariant} className="ml-2">
              {config.badge}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="pt-0 space-y-4">
          {/* Days Left Warning */}
          {daysUntilExpiry !== null && (
            <div className={cn(
              "p-3 rounded-lg border",
              isExpired 
                ? "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800"
                : isUrgent
                  ? "bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800"
                  : daysUntilExpiry <= 30
                    ? "bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800"
                    : "bg-gray-50 border-gray-200 dark:bg-gray-900/20 dark:border-gray-800"
            )}>
              <div className="flex items-center gap-2">
                {isExpired ? (
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                ) : isUrgent ? (
                  <Clock className="h-4 w-4 text-amber-500" />
                ) : (
                  <Clock className="h-4 w-4 text-blue-500" />
                )}
                <span className={cn(
                  "text-sm font-medium",
                  isExpired ? "text-red-700 dark:text-red-300" :
                  isUrgent ? "text-amber-700 dark:text-amber-300" :
                  "text-blue-700 dark:text-blue-300"
                )}>
                  {isExpired 
                    ? 'Licence expirée - Renouvellement nécessaire'
                    : isUrgent 
                      ? `${daysUntilExpiry} jour${daysUntilExpiry > 1 ? 's' : ''} restant${daysUntilExpiry > 1 ? 's' : ''} - Renouvellement recommandé`
                      : daysUntilExpiry <= 30
                        ? `${daysUntilExpiry} jours restants`
                        : 'Licence valide pour encore longtemps'
                  }
                </span>
              </div>

              {/* Progress bar for days left */}
              {daysUntilExpiry > 0 && (
                <div className="mt-2">
                  <Progress 
                    value={Math.max(0, Math.min(100, (daysUntilExpiry / 365) * 100))} 
                    className="h-2"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>0j</span>
                    <span>365j</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* License Key (Desktop only) */}
          {isDesktop && license?.license_key && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Clé de licence</p>
              <code className="block px-3 py-2 bg-muted rounded-md font-mono text-sm truncate">
                {license.license_key}
              </code>
            </div>
          )}

          {/* Action Button */}
          <Button 
            variant={(config.urgent || isUrgent || isExpired) ? "default" : "outline"} 
            className="w-full"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            {isExpired 
              ? 'Renouveler maintenant'
              : isUrgent 
                ? 'Gérer l\'abonnement'
                : 'Voir les détails'
            }
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    </Link>
  );
}

/**
 * Compact License Status Badge - For headers/toolbars
 * Shows just the status with icon, clickable to subscription
 */
export function LicenseStatusBadge({ showLabel = true }: { showLabel?: boolean }) {
  const { licenseState, daysUntilExpiry: getDaysUntilExpiry } = useLicenseStore();
  const isDesktop = isTauri();
  const daysUntilExpiry = typeof getDaysUntilExpiry === 'function' ? getDaysUntilExpiry() : getDaysUntilExpiry;

  const getConfig = () => {
    switch (licenseState) {
      case 'valid':
        return {
          icon: ShieldCheck,
          label: isDesktop ? 'Licence OK' : 'Abonnement OK',
          color: 'text-green-500',
          bgColor: 'bg-green-500/10',
          urgent: false,
        };
      case 'verification_needed':
        return {
          icon: ShieldAlert,
          label: 'Vérification',
          color: 'text-amber-500',
          bgColor: 'bg-amber-500/10',
          urgent: true,
        };
      case 'expired':
        return {
          icon: ShieldX,
          label: 'Expiré',
          color: 'text-red-500',
          bgColor: 'bg-red-500/10',
          urgent: true,
        };
      default:
        return {
          icon: ShieldCheck,
          label: 'Licence',
          color: 'text-gray-500',
          bgColor: 'bg-gray-500/10',
          urgent: false,
        };
    }
  };

  const config = getConfig();
  const StatusIcon = config.icon;
  const isUrgent = daysUntilExpiry !== null && daysUntilExpiry <= 7 && daysUntilExpiry > 0;

  return (
    <Link to="/settings/subscription">
      <div className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-full cursor-pointer transition-colors hover:bg-accent",
        config.bgColor
      )}>
        <StatusIcon className={cn("h-4 w-4", config.color)} />
        {showLabel && (
          <span className={cn("text-sm font-medium", config.color)}>
            {config.label}
          </span>
        )}
        {(config.urgent || isUrgent) && (
          <span className="flex h-2 w-2 relative">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
          </span>
        )}
        <ExternalLink className="h-3 w-3 text-muted-foreground ml-1" />
      </div>
    </Link>
  );
}

export default LicenseStatusCard;
